import type { MutationFilters, QueryClient } from '@tanstack/react-query';

/**
 * Every save of an entry goes through `useUpdateTimeEntry`, under this key —
 * a Show entries cell's and the Edit dialog's alike — so a row can find the
 * saves of its entry in the mutation cache, whichever component started them
 * and whether or not that component is still on screen. [HW-974]
 */
export const ENTRY_UPDATE_KEY = ['time-entries', 'update'] as const;

/**
 * Every delete of an entry goes through `useDeleteTimeEntry`, under this key,
 * for the same reason: a row whose delete is out stays shut, so its trash
 * cannot send a second Delete for an entry already gone. [HW-974]
 */
export const ENTRY_DELETE_KEY = ['time-entries', 'delete'] as const;

const sameKey = (key: readonly unknown[] | undefined, wanted: readonly unknown[]) =>
  key !== undefined && key.length === wanted.length && key.every((part, i) => part === wanted[i]);

/**
 * The writes of one entry still out: its saves (called with the entry and its
 * fields) and its delete (called with the id alone). With `untilRefetched` a
 * write stays out until the refetch it started has landed, so a row shut
 * while this matches opens again only on the report as the write left it. A
 * `.ts` file because vitest is node-env in this repo. [HW-974]
 */
export function entryWriteFilter(entryId: string): MutationFilters {
  return {
    predicate: (mutation) => {
      const key = mutation.options.mutationKey;
      const variables: unknown = mutation.state.variables;
      if (sameKey(key, ENTRY_UPDATE_KEY)) {
        return (variables as { id?: unknown } | undefined)?.id === entryId;
      }
      return sameKey(key, ENTRY_DELETE_KEY) && variables === entryId;
    },
  };
}

/**
 * Whether the confirmation for `entryId` should read "Deleting...". Show
 * entries shares one delete mutation across its rows, so `isPending` says a
 * delete is out, not whose: only the entry the mutation was called with is
 * pending. [HW-974]
 */
export function isDeletePendingFor(
  isPending: boolean,
  variables: unknown,
  entryId: string | undefined
): boolean {
  return isPending && entryId !== undefined && variables === entryId;
}

/** The cached reports' key prefix: `timeEntryKeys.reports()`. */
const REPORT_KEY_PREFIX = ['time-entries', 'report'] as const;

const isReportQuery = (queryKey: readonly unknown[]) =>
  REPORT_KEY_PREFIX.every((part, i) => queryKey[i] === part);

/**
 * What an entry write refreshes: every cached time-entries read is marked
 * stale and its active ones refetch, but only the active reports are waited
 * for — the returned promise settles when they have landed. One slow or
 * hanging unrelated read (a dropdown's list, say) must not keep a Show entries
 * row locked, and the reports are what the row is drawn from. [HW-974]
 */
export function refreshAfterEntryWrite(client: QueryClient): Promise<void> {
  void client.invalidateQueries({
    queryKey: ['time-entries'],
    predicate: (query) => !isReportQuery(query.queryKey),
  });
  return client.invalidateQueries({ queryKey: REPORT_KEY_PREFIX });
}
